// Full Technical Blueprint content — The Next O Technical Academy: Elite Technical Training System, Volume 1: Ball Mastery Blueprint
// Blocks are typed and rendered by ChapterRenderer.

import { CONTENT_IMAGES } from "./assets";

export const BOOK = {
  academy: "The Next O Technical Academy",
  title: "Elite Technical Training System",
  blueprint: "Volume 1: Ball Mastery Blueprint",
  volume: "Volume 1",
  subtitle: "Ball Mastery Blueprint",
  tagline: "Master the Ball. Master the Game.",
  author: "Dwain Barker",
  authorRole: "Founder & Technical Director",
};

export const CHAPTERS = [
  {
    id: "philosophy", num: "01", title: "The Next O Philosophy",
    kicker: "Every Touch Has a Purpose",
    minutes: 4,
    blocks: [
      { type: "quote", text: "The ball doesn't care how athletic you are. It responds to technique." },
      { type: "p", text: "At Next O Soccer, we believe technical excellence is built through focused repetition and intentional training. Every drill should develop a specific skill, and every session should move you one step closer to becoming a complete player." },
      { type: "h", text: "The Five Principles" },
      { type: "statgrid", items: [
        { label: "01", value: "Train with purpose" },
        { label: "02", value: "Master the fundamentals" },
        { label: "03", value: "Develop both feet" },
        { label: "04", value: "Play with your head up" },
        { label: "05", value: "Pursue progress every day" },
      ]},
      { type: "callout", title: "Our Core Message", text: "Technical training strengthens players, which strengthens teams." },
      { type: "h", text: "How To Use This Workbook" },
      { type: "p", text: "This workbook is designed to help players build technical consistency through structured, intentional practice. Each chapter includes instruction, coaching cues, drill diagrams, skill challenges, progress tracking, and reflection prompts." },
      { type: "cues", title: "Recommended Schedule", items: ["3–5 sessions per week", "30–60 minutes per session", "Focus on quality before speed", "Track your progress weekly"] },
      { type: "reflection", id: "philosophy-1", prompt: "Which of the five principles do you most need to commit to this month?" },
    ],
  },
  {
    id: "next-o-standard", num: "", title: "The Next O Standard",
    kicker: "More Than a Letter. It's a Mindset.",
    minutes: 5,
    blocks: [
      { type: "quote", text: "More than a letter. It's a mindset. Master the ball. Master the game." },
      { type: "p", text: "The Next O Standard is the identity every Next O Soccer player carries onto the field. It is built on five commitments — the five O's — that separate elite players from everyone else." },
      { type: "purposegrid", items: [
        { title: "Opportunity", text: "Every training session is an opportunity. Every touch, mistake, challenge, and repetition is a chance to improve. NEXT O STANDARD: Never waste a training session." },
        { title: "Observation", text: "See the game before it happens. Elite players constantly scan space, defenders, teammates, passing lanes, and their next action. NEXT O STANDARD: Play with your eyes before your feet." },
        { title: "Ownership", text: "Own your development. Nobody can train for you — show up prepared, stay disciplined, and hold yourself accountable. NEXT O STANDARD: Your progress is your responsibility." },
        { title: "Optimism", text: "Confidence is trained. Mistakes don't define you, your response does — stay positive, stay resilient, keep growing. NEXT O STANDARD: Believe before you achieve." },
        { title: "Outwork", text: "Effort separates players. Talent opens doors; work ethic keeps them open — choose discipline every day. NEXT O STANDARD: Outwork yesterday." },
      ]},
      { type: "callout", title: "The Next O Promise", text: "This is the standard you hold yourself to — on and off the field." },
      { type: "checklist", title: "The Next O Promise", persist: true, id: "next-o-promise", items: [
        "I will train with purpose.",
        "I will embrace challenges.",
        "I will respect the game.",
        "I will develop both feet.",
        "I will compete with integrity.",
        "I will never stop improving.",
        "I will represent Next O Soccer with pride on and off the field.",
      ]},
      { type: "reflection", id: "next-o-standard-1", prompt: "Which of the five O's will you focus on hardest this season, and why?" },
    ],
  },
  {
    id: "fundamentals", num: "02", title: "Ball Mastery Fundamentals",
    kicker: "Master the Ball. Master the Game.",
    minutes: 12,
    blocks: [
      { type: "quote", text: "Train slowly until your technique is perfect. Speed comes naturally after consistency." },
      { type: "h", text: "Why Ball Mastery Matters" },
      { type: "p", text: "Ball mastery is the ability to control, manipulate, and move the soccer ball confidently using every surface of both feet while maintaining balance, awareness, and composure. Elite players don't simply react to the ball — they command it." },
      { type: "p", text: "The objective of ball mastery is to make the ball feel like a natural extension of your body. When your technique becomes automatic, your mind is free to read the game, anticipate movement, and make faster decisions." },
      { type: "checklist", title: "Benefits of Ball Mastery", persist: false, items: ["Greater confidence under pressure", "Cleaner first touches", "Faster changes of direction", "Better passing accuracy", "Increased creativity", "Improved possession", "Stronger weak foot", "More attacking opportunities"] },
      { type: "image", src: CONTENT_IMAGES.ballControl, caption: "Every surface. Both feet. Total control." },

      { type: "h", text: "The Next O Touch Principle" },
      { type: "p", text: "Too many players simply 'kick the ball around.' Elite players know why every touch exists. Every touch should accomplish one of five objectives." },
      { type: "purposegrid", items: [
        { title: "Control", text: "Bring the ball under control immediately — receiving a pass before pressure arrives." },
        { title: "Move", text: "Move the ball into a better position — creating a better passing angle." },
        { title: "Protect", text: "Shield the ball from defenders — using your body while dribbling." },
        { title: "Attack", text: "Move forward with purpose — breaking a defensive line." },
        { title: "Create", text: "Generate space for yourself or teammates — using a feint or change of direction." },
      ]},
      { type: "callout", title: "Ask Yourself", text: "Why am I taking this touch? If you can't answer that, it probably wasn't the correct touch." },

      { type: "h", text: "Balance — The Secret Weapon" },
      { type: "p", text: "Most players think great footwork begins with quick feet. It actually begins with balance. Without it, your touches become heavy, your passes lose accuracy, and you can't explode after changing direction." },
      { type: "cues", title: "The Athletic Position", items: ["Knees bent", "Chest slightly forward", "Weight on the balls of your feet", "Relaxed shoulders", "Eyes forward"] },
      { type: "checklist", title: "Balance Checklist", persist: false, items: ["Head steady", "Knees bent", "Feet shoulder width", "Eyes up", "Center of gravity low"] },

      { type: "h", text: "Body Position" },
      { type: "p", text: "Your body shape determines your passing options, turning speed, first-touch direction, vision, and balance — before your feet ever touch the ball." },
      { type: "purposegrid", items: [
        { title: "Closed Shape", text: "Best when shielding the ball from a defender." },
        { title: "Open Shape", text: "Best when receiving — lets you see the whole field." },
        { title: "Side-On", text: "Best for changing direction quickly." },
      ]},
      { type: "callout", title: "Coach Tip", text: "Receive sideways whenever possible." },
      { type: "image", src: CONTENT_IMAGES.duel, caption: "Body shape decides the duel before your feet do." },

      { type: "h", text: "Using Both Feet" },
      { type: "p", text: "Elite players are unpredictable. One-footed players become easy to defend. The training goal is simple: every drill, 50% right foot and 50% left foot." },
      { type: "checklist", title: "Weak Foot Challenge (no switching feet)", persist: false, items: ["100 Foundations", "100 Toe Taps", "50 Pull Push", "50 Sole Rolls"] },

      { type: "h", text: "Head Up — See The Game Before It Happens" },
      { type: "p", text: "Great players don't stare at the ball. They scan constantly — before receiving, while dribbling, before passing, before shooting, and after passing." },
      { type: "callout", title: "Coach Cue", text: "Ball… Scan… Ball… Scan…" },

      { type: "h", text: "First Touch Quality" },
      { type: "p", text: "The first touch determines everything. A poor first touch creates pressure; a great first touch creates options. Every first touch should have Direction, Distance, Purpose, and Speed." },

      { type: "h", text: "Repetition" },
      { type: "statgrid", items: [
        { label: "Beginner", value: "500 / wk" },
        { label: "Intermediate", value: "1,000 / wk" },
        { label: "Advanced", value: "2,000+ / wk" },
      ]},
      { type: "p", text: "These are purposeful touches per week — not just random contacts." },

      { type: "h", text: "Common Mistakes" },
      { type: "mistakes", items: ["Looking down", "Standing upright", "Large touches", "Using only your dominant foot", "Training too fast", "Ignoring technique", "Training with no purpose"] },

      { type: "h", text: "Next O Technical Standards" },
      { type: "benchmark", rows: [
        { skill: "Toe Taps", bronze: 50, silver: 100, gold: 250, elite: 500 },
        { skill: "Foundations", bronze: 50, silver: 100, gold: 250, elite: 500 },
        { skill: "Sole Rolls", bronze: 25, silver: 50, gold: 100, elite: 200 },
        { skill: "Pull Push", bronze: 25, silver: 50, gold: 100, elite: 200 },
        { skill: "V Pull", bronze: 25, silver: 50, gold: 100, elite: 200 },
        { skill: "Juggling", bronze: 10, silver: 25, gold: 50, elite: 100 },
      ]},

      { type: "h", text: "Pre-Training Checklist" },
      { type: "checklist", title: "Before every session", persist: true, id: "pretraining", items: ["Ball", "Water", "Cones", "Cleats", "Positive Mindset", "Goal for Today", "Notebook"] },

      { type: "reflection", id: "fundamentals-1", prompt: "What area of your ball control needs the most improvement right now?" },
    ],
  },
  {
    id: "technical-touches", num: "03", title: "Technical Touches",
    kicker: "Small Touches Create Big Results",
    minutes: 15,
    blocks: [
      { type: "quote", text: "Master the simple touches first. The advanced skills become easier because of them." },
      { type: "p", text: "Technical touches are the building blocks of elite ball mastery. Before you can consistently beat defenders with advanced moves, you must develop comfort manipulating the ball with every surface of both feet." },
      { type: "cues", title: "Chapter Objectives", items: ["Improve foot speed", "Develop balance and coordination", "Increase confidence with both feet", "Build habits that transfer to games", "Prepare for advanced moves"] },
      { type: "h", text: "The Foundational Moves" },
      { type: "p", text: "Tap any move to open its full breakdown — an animated drill diagram, numbered step-by-step execution, coaching cues, common mistakes, and Bronze → Elite standards." },
      { type: "chapter_video", chapterId: "technical-touches", title: "Watch: Foundational Touches In Action" },
      { type: "movesgrid", tier: "touch" },
    ],
  },
  {
    id: "technical-moves", num: "04", title: "Technical Moves",
    kicker: "Beat Defenders With Purpose",
    minutes: 18,
    blocks: [
      { type: "quote", text: "The move doesn't beat the defender. Your commitment to it does." },
      { type: "p", text: "These are the signature moves that define elite attacking players — from the Cruyff Turn to the Elastico. Each combines deception, timing, and explosive acceleration. Master the fundamentals first, then bring these to life." },
      { type: "callout", title: "Used By The Pros", text: "Every move includes the professional who made it famous and the exact match moment where it's most effective." },
      { type: "h", text: "The Signature Moves" },
      { type: "chapter_video", chapterId: "technical-moves", title: "Watch: Signature Moves In Action" },
      { type: "movesgrid", tier: "move" },
    ],
  },
  {
    id: "combination-moves", num: "", title: "Combination & Flow Moves",
    kicker: "Link Moves. Break Lines.",
    minutes: 14,
    blocks: [
      { type: "quote", text: "One move gets a reaction. Two linked moves get you past." },
      { type: "p", text: "Combination (flow) moves chain two skills into one seamless sequence. This is where intermediate players become advanced — the first move shifts the defender, and the second one beats them. The key is flow: no pause between the touches." },
      { type: "callout", title: "The Next O Way", text: "Master each move on its own first. Then link them until the sequence feels like a single move." },
      { type: "image", src: CONTENT_IMAGES.duel, caption: "Chain your touches. Never let the defender reset." },
      { type: "h", text: "Flow Combinations" },
      { type: "p", text: "Tap any combination to see its animated flow diagram and step-by-step breakdown." },
      { type: "chapter_video", chapterId: "combination-moves", title: "Watch: Combination Flows In Action" },
      { type: "movesgrid", tier: "combo" },
      { type: "reflection", id: "combination-1", prompt: "Which combination feels most natural, and which needs the most reps to flow smoothly?" },
    ],
  },
  {
    id: "drills", num: "05", title: "Ball Mastery Drills",
    kicker: "Structured Repetition Builds Mastery",
    minutes: 8,
    blocks: [
      { type: "p", text: "Drills turn technique into instinct. Use these structured sessions to accumulate purposeful touches every week. Quality before speed — always." },
      { type: "image", src: CONTENT_IMAGES.cones, caption: "Set your cones. Attack every rep with intent." },
      { type: "h", text: "The Next O Ball Mastery Circuit" },
      { type: "p", text: "Complete the following without stopping. Record your time and aim to improve every week." },
      { type: "checklist", title: "The Circuit", persist: true, id: "circuit", items: [
        "100 Foundations", "100 Toe Taps",
        "50 Inside-Outside (Right)", "50 Inside-Outside (Left)",
        "50 Sole Rolls (Right)", "50 Sole Rolls (Left)",
        "50 Pull Push (Right)", "50 Pull Push (Left)",
        "25 V Pulls (Right)", "25 V Pulls (Left)",
        "25 L Turns (Right)", "25 L Turns (Left)",
        "10 Scissors", "10 Double Scissors", "10 Cruyff Turns",
        "10 Maradona Turns", "10 Elastico", "10 Ronaldo Chops", "10 La Croqueta",
      ]},
      { type: "h", text: "Dynamic Warm-Up (10 Minutes)" },
      { type: "purposegrid", items: [
        { title: "High Knees", text: "Drive your knees up, stay tall and light." },
        { title: "Butt Kicks", text: "Heels to glutes, quick turnover." },
        { title: "Carioca", text: "Lateral crossover to open the hips." },
        { title: "Hip Openers", text: "Open and close the gates while moving." },
        { title: "Leg Swings", text: "Front-to-back and side-to-side control." },
        { title: "Light Ball Touches", text: "Foundations and toe taps to wake up your feet." },
      ]},
      { type: "reflection", id: "drills-1", prompt: "Log your best circuit time this week and your target for next week." },
    ],
  },
  {
    id: "balance", num: "06", title: "Balance & Coordination",
    kicker: "Control Starts With Your Body",
    minutes: 6,
    blocks: [
      { type: "p", text: "Balance and coordination are the invisible engine behind every clean touch. Elite players stay low, stable, and light on their feet so they can change direction instantly." },
      { type: "cues", title: "Balance Builders", items: ["Single-leg holds (30s each leg)", "Sole rolls on one foot", "Toe taps with eyes closed", "Hop-and-stick landings", "Slow-motion moves for control"] },
      { type: "chapter_video", chapterId: "balance", title: "Watch: Balance & Coordination Workout" },
      { type: "h", text: "The Next O Balance Training System" },
      { type: "p", text: "This is what separates Next O Technical Academy training from a basic training manual. Balance is trained in three progressive levels — earn your way up. Master every drill at your level before moving on." },
      { type: "purposegrid", items: [
        { title: "Beginner", text: "Single-leg stand (30s each leg) · Two-foot ball taps on top of the ball · Sole rolls standing tall · Slow foundations staying balanced · Hop-and-stick landings (2 feet)." },
        { title: "Intermediate", text: "Single-leg ball taps (30s each) · One-foot sole rolls · Toe taps with eyes up · Single-leg hop-and-stick landings · Slow-motion scissors holding the finish position." },
        { title: "Advanced", text: "Single-leg balance with ball juggling · Eyes-closed sole rolls · Move → freeze → hold 3s on one leg · Reactive single-leg landings from a jump · Full-speed moves finishing balanced and still." },
      ]},
      { type: "callout", title: "Progression Rule", text: "You only level up when you can complete every drill in your tier cleanly for 3 sessions in a row." },
      { type: "benchmark", rows: [
        { skill: "Single-Leg Hold", bronze: "20s", silver: "30s", gold: "45s", elite: "60s" },
        { skill: "1-Foot Sole Rolls", bronze: 15, silver: 30, gold: 60, elite: 100 },
        { skill: "Freeze Balance (per move)", bronze: "1s", silver: "2s", gold: "3s", elite: "4s+" },
      ]},
      { type: "checklist", title: "Coordination Standards", persist: false, items: ["Steady head through every move", "Smooth weight transfer", "No stumbles on direction changes", "Balanced on both feet equally"] },
      { type: "callout", title: "Coach Tip", text: "If you can't do it slowly with balance, you can't do it fast under pressure." },
      { type: "reflection", id: "balance-1", prompt: "Which balance drill was hardest, and how will you practice it this week?" },
    ],
  },
  {
    id: "vision", num: "07", title: "Vision & Scanning",
    kicker: "See The Game Before It Happens",
    minutes: 6,
    blocks: [
      { type: "p", text: "The best players have already decided what to do before the ball arrives. Vision is a trainable habit built through constant scanning." },
      { type: "image", src: CONTENT_IMAGES.youth, caption: "Head up. Read the picture before it arrives." },
      { type: "chapter_video", chapterId: "vision", title: "Watch: Vision & Scanning Drills" },
      { type: "checklist", title: "Scanning Checklist", persist: false, items: ["Scan before receiving", "Scan while dribbling", "Scan before passing", "Scan before shooting", "Scan after passing"] },
      { type: "callout", title: "Coach Cue", text: "Ball… Scan… Ball… Scan…" },
      { type: "cues", title: "Vision Drills", items: ["Number call-outs while dribbling", "Head-up foundations", "Peripheral passing squares", "Two-touch with a scan between touches"] },
      { type: "reflection", id: "vision-1", prompt: "How many times do you scan before receiving? Set a target and track it in your next game." },
    ],
  },
  {
    id: "agility", num: "08", title: "Agility & Speed",
    kicker: "Explode Out Of Every Move",
    minutes: 7,
    blocks: [
      { type: "p", text: "A move only matters if you can accelerate out of it. Agility and speed turn technical touches into game-changing actions." },
      { type: "image", src: CONTENT_IMAGES.player, caption: "The move creates the space — acceleration takes it." },
      { type: "chapter_video", chapterId: "agility", title: "Watch: Agility & Speed Workout" },
      { type: "cues", title: "Agility Work", items: ["Ladder quick feet", "Cone slalom with the ball", "Change-of-direction sprints", "Reactive first-step drills", "Move → explode into a 10-yard sprint"] },
      { type: "statgrid", items: [
        { label: "First Step", value: "Explosive" },
        { label: "5-10 yds", value: "Acceleration" },
        { label: "Recovery", value: "Full effort" },
      ]},
      { type: "callout", title: "Key Principle", text: "The move creates the space. Your acceleration takes it." },
      { type: "reflection", id: "agility-1", prompt: "Which move do you most need to add explosive acceleration to?" },
    ],
  },
  {
    id: "challenges", num: "09", title: "Challenges",
    kicker: "Prove Your Progress",
    minutes: 5,
    blocks: [
      { type: "p", text: "Challenges turn training into measurable progress. Test yourself, record your numbers, and chase the next tier." },
      { type: "h", text: "Foundation Challenge" },
      { type: "checklist", title: "Complete without stopping", persist: true, id: "chapter-challenge", items: [
        "100 Toe Taps", "100 Foundations",
        "50 Inside-Outside (each foot)", "50 Sole Rolls (each foot)",
        "50 Pull Pushes (each foot)", "50 V Pulls (each foot)",
      ]},
      { type: "h", text: "Track Your Elite Standards" },
      { type: "benchmark", rows: [
        { skill: "Toe Taps", bronze: 50, silver: 100, gold: 250, elite: 500 },
        { skill: "Foundations", bronze: 50, silver: 100, gold: 250, elite: 500 },
        { skill: "Juggling", bronze: 10, silver: 25, gold: 50, elite: 100 },
      ]},
      { type: "h", text: "Technical Moves Challenge" },
      { type: "p", text: "Prove your 1v1 moves. Complete each one cleanly — controlled touch, sold fake, explosive exit — before you chase speed." },
      { type: "checklist", title: "Complete cleanly (both feet where shown)", persist: true, id: "technical-moves-challenge", items: [
        "10 Cruyff Turns (each foot)", "10 Scissors (each foot)",
        "10 Double Scissors (each foot)", "10 Elastico (each foot)",
        "10 La Croqueta (each direction)", "10 Ronaldo Chops (each foot)",
        "10 Maradona Spins (each direction)", "10 Hezzy (each foot)",
      ]},
      { type: "benchmark", rows: [
        { skill: "Scissors (clean, in a row)", bronze: 5, silver: 10, gold: 20, elite: 40 },
        { skill: "Elastico (clean, each foot)", bronze: 3, silver: 6, gold: 12, elite: 25 },
        { skill: "Cruyff Turn (clean, in a row)", bronze: 5, silver: 10, gold: 20, elite: 40 },
      ]},
      { type: "reflection", id: "challenges-moves", prompt: "Which technical move is your most reliable in a game, and which one still breaks down under speed?" },
      { type: "h", text: "Combination & Flow Moves Challenge" },
      { type: "p", text: "Link two skills into one seamless sequence with no pause between touches. The first move shifts the defender; the second one beats them." },
      { type: "checklist", title: "Complete with no pause between touches", persist: true, id: "combination-moves-challenge", items: [
        "10 Rollover + Scissors (each side)", "10 Rollover + Stepover (each foot)",
        "10 Rollover + Ronaldo Chop (each foot)", "10 Rollover + Drag Back (each foot)",
        "10 Hezzy + Scissors (each side)", "5 Rabona Turn + Scissors (each side)",
        "5 Rabona Turn + Elastico (each foot)",
      ]},
      { type: "benchmark", rows: [
        { skill: "Rollover + Scissors (fluid reps)", bronze: 4, silver: 8, gold: 15, elite: 30 },
        { skill: "Hezzy + Scissors (fluid reps)", bronze: 3, silver: 6, gold: 12, elite: 25 },
        { skill: "Rabona + Elastico (fluid reps)", bronze: 2, silver: 4, gold: 8, elite: 15 },
      ]},
      { type: "reflection", id: "challenges-combos", prompt: "Which combination flows most smoothly for you, and which needs the most reps before it feels like one move?" },
      { type: "reflection", id: "challenges-1", prompt: "Record your total time, your strongest move, and your weakest move." },
    ],
  },
  {
    id: "journal", num: "10", title: "Player Journal",
    kicker: "Keep Training. Keep Learning. Keep Moving Forward.",
    minutes: 5,
    blocks: [
      { type: "p", text: "Success in soccer isn't built overnight. It's earned through discipline, consistency, and a commitment to improving every day. Use this daily log to reflect, track your weak-foot progress, and stay accountable." },
      { type: "journal_app" },
      { type: "callout", title: "Remember", text: "This workbook is dedicated to every player willing to put in the extra touches, embrace the process, and chase excellence one session at a time." },
    ],
  },
];

export const getChapter = (id) => CHAPTERS.find((c) => c.id === id);
